import { Strong } from '@buffer-mono/popcorn'
import { getBillingUrl } from '~publish/helpers/navigateToBilling'
import { navigateTo } from '~publish/helpers/navigation'
import { HC_UTM_PARAMS } from '~publish/legacy/utils/contants'

// buffer.com (marketing) and support.buffer.com have no BIBE / staging
// deployments, so they stay as literal URLs. The Publish billing URL is
// BIBE-aware.
export const URLS = {
  SIGN_UP_URL: 'https://buffer.com/pricing/publish',
  SUPPORT_URL: getBillingUrl('topNav-migration-cta'),
  MARKETING_URL: 'https://buffer.com/publish',
  CONTACT_SUPPORT_URL: `https://support.buffer.com/?openContact=true&${HC_UTM_PARAMS}`,
}

type AccessAction = {
  label: string
  onClick: () => void
}

type AccessTypeTranslation = {
  description1: string
  description2: string
  description3?: string
  secondaryLabel: string
}

type AccessTypeWithPrimaryActionTranslation = AccessTypeTranslation & {
  primaryLabel: string
}

export type MissingAccessTranslations = {
  accessTypes: {
    admin: AccessTypeWithPrimaryActionTranslation
    adminWithOrgAccess: AccessTypeWithPrimaryActionTranslation
    nonAdmin: AccessTypeTranslation
    nonAdminWithOrgAccess: AccessTypeWithPrimaryActionTranslation
  }
}

type AccessTypeContent = {
  primaryAction?: AccessAction
  secondaryAction: AccessAction
  description: JSX.Element
}

type AccessType =
  | 'adminWithOrgAccess'
  | 'admin'
  | 'nonAdminWithOrgAccess'
  | 'nonAdmin'

type AccessContent = Record<AccessType, AccessTypeContent>

const getContent = ({
  switchOrganization,
  translations,
  orgName,
  orgNameWithAccess,
  orgIdWithAccess,
}: {
  switchOrganization: (orgId: string) => void
  translations: MissingAccessTranslations
  orgName: string | null
  orgNameWithAccess?: string
  orgIdWithAccess?: string
}): AccessContent => {
  const { adminWithOrgAccess, nonAdminWithOrgAccess, admin, nonAdmin } =
    translations.accessTypes
  return {
    admin: {
      primaryAction: {
        label: admin.primaryLabel,
        onClick: (): void => navigateTo(URLS.SIGN_UP_URL),
      },
      secondaryAction: {
        label: admin.secondaryLabel,
        onClick: (): void => navigateTo(URLS.MARKETING_URL),
      },
      description: (
        <>
          {admin.description1}
          <Strong>{` ${orgName} `}</Strong>
          {admin.description2}
        </>
      ),
    },
    adminWithOrgAccess: {
      primaryAction: {
        label: `${adminWithOrgAccess.primaryLabel} ${orgNameWithAccess}`,
        onClick: (): void => {
          if (!orgIdWithAccess) return
          switchOrganization(orgIdWithAccess)
        },
      },
      secondaryAction: {
        label: adminWithOrgAccess.secondaryLabel,
        onClick: (): void => navigateTo(URLS.SIGN_UP_URL),
      },
      description: (
        <>
          {adminWithOrgAccess.description1}
          <Strong>{` ${orgName} `}</Strong>
          {adminWithOrgAccess.description2}
          <Strong>{` ${orgNameWithAccess} `}</Strong>
          {adminWithOrgAccess.description3}
        </>
      ),
    },
    nonAdmin: {
      secondaryAction: {
        label: nonAdmin.secondaryLabel,
        onClick: (): void => navigateTo(URLS.CONTACT_SUPPORT_URL),
      },
      description: (
        <>
          {nonAdmin.description1}
          <Strong>{` ${orgName} `}</Strong>
          {nonAdmin.description2}
        </>
      ),
    },
    nonAdminWithOrgAccess: {
      primaryAction: {
        label: `${nonAdminWithOrgAccess.primaryLabel} ${orgNameWithAccess}`,
        onClick: (): void => {
          if (!orgIdWithAccess) return
          switchOrganization(orgIdWithAccess)
        },
      },
      secondaryAction: {
        label: nonAdminWithOrgAccess.secondaryLabel,
        onClick: (): void => navigateTo(URLS.SUPPORT_URL),
      },
      description: (
        <>
          {nonAdminWithOrgAccess.description1}
          <Strong>{` ${orgName} `}</Strong>
          {nonAdminWithOrgAccess.description2}
          <Strong>{` ${orgNameWithAccess} `}</Strong>
          {nonAdminWithOrgAccess.description3}
        </>
      ),
    },
  }
}

const getAccessType = ({
  canManageBilling,
  hasOrgWithAccess,
}: {
  canManageBilling: boolean
  hasOrgWithAccess: boolean
}): AccessType => {
  if (canManageBilling) {
    return hasOrgWithAccess ? 'adminWithOrgAccess' : 'admin'
  }
  return hasOrgWithAccess ? 'nonAdminWithOrgAccess' : 'nonAdmin'
}

export const getTypeContent = ({
  switchOrganization,
  translations,
  canManageBilling,
  orgWithAccess,
  orgName,
}: {
  switchOrganization: (orgId: string) => void
  translations: MissingAccessTranslations
  canManageBilling: boolean
  orgWithAccess?: { id: string; name: string } | null
  orgName: string | null
}): AccessTypeContent => {
  const accessType = getAccessType({
    canManageBilling,
    hasOrgWithAccess: !!orgWithAccess,
  })
  const content = getContent({
    switchOrganization,
    translations,
    orgName,
    orgNameWithAccess: orgWithAccess?.name,
    orgIdWithAccess: orgWithAccess?.id,
  })
  return content[accessType]
}
